import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { BookingService } from "../../services/BookingService";
import { apiClient } from "../../utils/apiClient";
import { ConfirmationModal, SuccessModal, LogoutConfirmationModal } from "../common/Modal";
import "./UserDashboard.css";

// Mock user data
const userData = {
  fullName: "John Smith",
  mobileNumber: "+1 (555) 123-4567",
};

// API Response types - matching exact response structure
interface BookingItem {
  id: string;
  resource_type: "DESK" | "MEETING_ROOM";
  resource_id: string;
  resource_name: string;
  booking_type: "daily" | "hourly" | "monthly";
  start_datetime: string;
  end_datetime: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  item_status: string;
  cancelled_at: string | null;
}

interface ApiBooking {
  id: string;
  booking_code: string;
  user_name: string;
  mobile_number: string;
  base_amount: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  currency: string;
  status: string;
  razorpay_order_id: string | null;
  items: BookingItem[];
  created_at: string;
}

interface ApiBookingsResponse {
  current_bookings: ApiBooking[];
  previous_bookings: ApiBooking[];
}

// Transformed booking type for UI
interface Booking {
  id: string;
  bookingCode: string;
  resourceName: string;
  resourceType: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  bookingType: string;
  status: string;
  totalAmount: string;
  currency: string;
  items: BookingItem[];
  createdAt: string;
}

// Transform API booking to UI booking
const transformBooking = (apiBooking: ApiBooking): Booking => {
  const primaryItem = apiBooking.items[0]; // Use first item for primary display
  const startDate = new Date(primaryItem.start_datetime);
  const endDate = new Date(primaryItem.end_datetime);
  
  return {
    id: apiBooking.id,
    bookingCode: apiBooking.booking_code,
    resourceName: primaryItem.resource_name,
    resourceType: primaryItem.resource_type,
    startDate: startDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }),
    endDate: endDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }),
    startTime: startDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    }),
    endTime: endDate.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    }),
    bookingType: primaryItem.booking_type,
    status: apiBooking.status,
    totalAmount: apiBooking.total_amount,
    currency: apiBooking.currency,
    items: apiBooking.items,
    createdAt: apiBooking.created_at
  };
};

function BookingCard({ 
  booking, 
  showActions = false,
  onEdit,
  onCancel,
  cancellingItems = new Set()
}: { 
  booking: Booking; 
  showActions?: boolean;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  cancellingItems?: Set<string>;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#22c55e';
      case 'pending_payment': return '#f59e0b';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getResourceIcon = (type: string) => {
    return type === 'DESK' ? '🪑' : '🏢';
  };

  const isAnyCancelling = booking.items.some(item => cancellingItems.has(item.id));

  return (
    <div className="booking-card">
      <div className="booking-card-header">
        <div className="booking-card-title-section">
          <div className="booking-card-title-row">
            <h3 className="booking-card-title">
              {getResourceIcon(booking.resourceType)} {booking.resourceName}
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span className={`booking-type-badge ${booking.bookingType}`}>
                {booking.bookingType.charAt(0).toUpperCase() + booking.bookingType.slice(1)}
              </span>
              <span 
                className="booking-status-badge"
                style={{ backgroundColor: getStatusColor(booking.status), color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}
              >
                {booking.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
          </div>
          <div className="booking-card-details">
            <div className="booking-card-detail">
              <span>📅</span>
              <span>{booking.startDate === booking.endDate ? booking.startDate : `${booking.startDate} - ${booking.endDate}`}</span>
            </div>
            <div className="booking-card-detail">
              <span>🕐</span>
              <span>{booking.startTime} - {booking.endTime}</span>
            </div>
            <div className="booking-card-detail">
              <span>💰</span>
              <span>{booking.currency} {booking.totalAmount}</span>
            </div>
          </div>
        </div>
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className={`booking-card-expand-btn ${isExpanded ? 'expanded' : ''}`}
          aria-label="Toggle details"
        >
          ▼
        </button>
      </div>
      
      {isExpanded && (
        <div className="booking-card-expanded">
          <div className="booking-card-expanded-content">
            <div className="booking-card-grid">
              <div className="booking-card-grid-item">
                <p className="booking-card-grid-label">Booking Code</p>
                <p className="booking-card-grid-value">{booking.bookingCode}</p>
              </div>
              <div className="booking-card-grid-item">
                <p className="booking-card-grid-label">Resource Type</p>
                <p className="booking-card-grid-value">{booking.resourceType.replace('_', ' ')}</p>
              </div>
            </div>
            {booking.items.length > 1 && (
              <div>
                <p className="booking-card-grid-label">All Items ({booking.items.length})</p>
                <div className="booking-items-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {booking.items.map((item) => (
                    <div key={item.id} className="booking-item" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      <span>{getResourceIcon(item.resource_type)} {item.resource_name} ({item.resource_type})</span>
                      <span className="booking-item-amount">{booking.currency} {item.total_amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {showActions && booking.status === 'confirmed' && (
            <div className="booking-card-expanded-actions">
              <button 
                onClick={() => onCancel?.(booking.id)}
                className="btn btn-danger"
                disabled={isAnyCancelling}
                style={{ 
                  opacity: isAnyCancelling ? 0.6 : 1,
                  cursor: isAnyCancelling ? 'not-allowed' : 'pointer'
                }}
              >
                {isAnyCancelling ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function UserDashboard({ onBack, onNewBooking }: { onBack?: () => void; onNewBooking?: () => void }) {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("current");
  const [bookingsData, setBookingsData] = useState<ApiBookingsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancellingItems, setCancellingItems] = useState<Set<string>>(new Set());
  
  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    bookingCode: string;
    item?: BookingItem;
    currency: string;
    isLoading: boolean;
  }>({ isOpen: false, bookingCode: '', item: undefined, currency: '', isLoading: false });
  
  const [successModal, setSuccessModal] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
  }>({ isOpen: false, title: '', description: '' });

  const [logoutModal, setLogoutModal] = useState<{
    isOpen: boolean;
    isLoading: boolean;
  }>({ isOpen: false, isLoading: false });

  const handleEditProfile = () => {
    console.log("Edit profile clicked");
  };

  const handleEditBooking = (id: string) => {
    console.log("Edit booking:", id);
  };

  const handleCancelBooking = async (bookingId: string) => {
    const booking = [...(bookingsData?.current_bookings || []), ...(bookingsData?.previous_bookings || [])]
      .find(b => b.id === bookingId);
    
    if (!booking) {
      setSuccessModal({
        isOpen: true,
        title: 'Error',
        description: 'Booking not found'
      });
      return;
    }

    // For now, handle single item booking (can be extended for multiple items later)
    const item = booking.items[0];
    
    setConfirmModal({
      isOpen: true,
      bookingCode: booking.booking_code,
      item: item,
      currency: booking.currency,
      isLoading: false
    });
  };

  const handleConfirmCancel = async () => {
    if (!confirmModal.item) return;
    
    // Show loading state
    setConfirmModal(prev => ({ ...prev, isLoading: true }));
    
    try {
      await cancelBookingItem(confirmModal.bookingCode, confirmModal.item.id);
    } finally {
      // Modal will be closed by success/error handling in cancelBookingItem
    }
  };

  const cancelBookingItem = async (bookingCode: string, itemId: string) => {
    setCancellingItems(prev => new Set(prev).add(itemId));
    
    try {
      console.log(`🗑️ Cancelling booking item: ${bookingCode}/${itemId}`);
      
      const response = await apiClient.delete(`/api/booking-orders/${bookingCode}/items/${itemId}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      console.log('✅ Booking item cancelled successfully:', response.data);
      
      // Close confirmation modal
      setConfirmModal({ isOpen: false, bookingCode: '', item: undefined, currency: '', isLoading: false });
      
      // Show success message
      const message = response.data?.message || 'Booking item cancelled successfully';
      const status = response.data?.order_status;
      
      let successTitle = 'Success';
      let successDescription = message;
      
      if (status === 'cancelled') {
        successDescription = 'Booking has been cancelled successfully.';
      } else if (status === 'partially_cancelled') {
        successDescription = 'Booking item cancelled. Your booking is now partially cancelled.';
      }
      
      setSuccessModal({
        isOpen: true,
        title: successTitle,
        description: successDescription
      });
      
      // Refresh bookings data
      await fetchBookings();
      
    } catch (error: any) {
      console.error('❌ Failed to cancel booking item:', error);
      
      // Close confirmation modal
      setConfirmModal({ isOpen: false, bookingCode: '', item: undefined, currency: '', isLoading: false });
      
      // Handle specific error cases
      let errorTitle = 'Cancellation Failed';
      let errorDescription = 'Failed to cancel booking item';
      
      if (error.status === 401) {
        errorTitle = 'Authentication Error';
        errorDescription = 'Please login again to continue.';
        setTimeout(() => logout?.(), 2000);
      } else if (error.status === 403) {
        errorDescription = 'You can only cancel your own bookings.';
      } else if (error.status === 404) {
        errorDescription = 'Booking or item not found. It may have already been cancelled.';
      } else if (error.status === 400) {
        const errorMessage = error.message || '';
        if (errorMessage.includes('already cancelled')) {
          errorDescription = 'This booking item is already cancelled.';
        } else if (errorMessage.includes('pending_payment')) {
          errorDescription = 'Cannot cancel items with pending payment status.';
        } else {
          errorDescription = `Cannot cancel booking: ${errorMessage}`;
        }
      }
      
      setSuccessModal({
        isOpen: true,
        title: errorTitle,
        description: errorDescription
      });
      
    } finally {
      setCancellingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  // Extract fetchBookings into separate function for reuse
  const fetchBookings = useCallback(async () => {
    if (!user?.mobileNumber) {
      console.warn('User mobile number not available');
      return;
    }
    
    setLoading(true);
    try {
      console.log('🔄 Fetching user bookings for:', user.mobileNumber);
      const bookings = await BookingService.getUserBookings(user.mobileNumber);
      
      if (bookings) {
        setBookingsData(bookings as any);
        console.log('✅ Bookings fetched successfully:', {
        current_length: bookings?.current_bookings?.length || 0,
        previous_length: bookings?.previous_bookings?.length || 0,
        current: bookings?.current_bookings,
        previous: bookings?.previous_bookings
      });
      }
    } catch (error) {
      console.error('❌ Failed to fetch bookings:', error);
      // Set empty data on error to prevent crashes
      setBookingsData({
        current_bookings: [],
        previous_bookings: []
      });
    } finally {
      setLoading(false);
    }
  }, [user?.mobileNumber]);

  const handleLogout = () => {
    console.log('Logout button clicked');
    setLogoutModal({ isOpen: true, isLoading: false });
  };

  const handleConfirmLogout = async () => {
    setLogoutModal(prev => ({ ...prev, isLoading: true }));
    try {
      // Small delay to show the loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      logout?.();
    } finally {
      setLogoutModal({ isOpen: false, isLoading: false });
    }
  };

  // Fetch bookings from API
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  if (!user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  const currentBookings = bookingsData?.current_bookings?.map(transformBooking) || [];
  const previousBookings = bookingsData?.previous_bookings?.map(transformBooking) || [];

  return (
    <div className="dashboard-container">
      {/* Header Bar */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div>
            <h1 className="dashboard-header-title">Dashboard</h1>
            <p className="dashboard-header-subtitle">Welcome back, {user.name?.split(' ')[0] || userData.fullName.split(' ')[0]}</p>
          </div>
          <div className="dashboard-header-buttons">
            <button 
                onClick={() => {
                  console.log('New Booking button clicked');
                  onNewBooking?.();
                }}
                className="px-8 py-3 bg-[#1F473E] text-[#F1ECDC] rounded hover:bg-[#49685E] transition-colors shadow-lg shadow-[#1F473E]/20"
              >
                New Booking
              </button>
             <button 
                onClick={handleLogout}
                className="px-8 py-3 border-2 border-[#1F473E] text-[#1F473E] rounded hover:bg-[#1F473E] hover:text-[#F1ECDC] transition-colors shadow-lg shadow-[#1F473E]/20"
              >
                Logout
              </button>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Account Section */}
        <div className="dashboard-card account-section">
          <h2 className="account-header">
            <div className="account-icon">👤</div>
            Account Information
          </h2>
          <div className="account-info">
            <div className="account-info-item">
              <p className="account-info-label">Full Name</p>
              <p className="account-info-value">{user.name || userData.fullName}</p>
            </div>
            <div className="account-info-item">
              <p className="account-info-label">Mobile Number</p>
              <p className="account-info-value">{user.mobileNumber || userData.mobileNumber}</p>
            </div>
          </div>
          <hr className="account-divider" />
          <button 
                onClick={handleEditProfile}
                className="px-5 py-2 bg-[#1F473E] text-[#F1ECDC] rounded hover:bg-[#49685E] transition-colors shadow-lg shadow-[#1F473E]/20"
            >
            Edit Profile
          </button>
        </div>

        {/* Booking Details Section */}
        <div className="dashboard-card booking-section">
          <h2 className="booking-section-title">Booking Management</h2>
          <p className="booking-section-description">View and manage your workspace reservations</p>
          
          {/* Tabs */}
          <div className="tabs-container">
            {["current", "previous"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`tab-button ${activeTab === tab ? 'active' : ''}`}
              >
                {tab === "current" ? "Current" : "Previous"}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="no-bookings">Loading bookings...</div>
          )}

          {/* Tab Content */}
          {!loading && (
            <div className="tab-content">
              {activeTab === "current" && (
                <>
                  <h3 className="tab-content-title">Current Bookings</h3>
                  {currentBookings.length > 0 ? (
                    currentBookings.map((booking) => (
                      <BookingCard 
                        key={booking.id} 
                        booking={booking} 
                        showActions={true}
                        onEdit={handleEditBooking}
                        onCancel={handleCancelBooking}
                        cancellingItems={cancellingItems}
                      />
                    ))
                  ) : (
                    <p className="no-bookings">No current bookings</p>
                  )}
                </>
              )}

              {activeTab === "previous" && (
                <>
                  <h3 className="tab-content-title">Previous Bookings</h3>
                  {previousBookings.length > 0 ? (
                    previousBookings.map((booking) => (
                      <BookingCard key={booking.id} booking={booking} />
                    ))
                  ) : (
                    <p className="no-bookings">No previous bookings</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Modals */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, bookingCode: '', item: undefined, currency: '', isLoading: false })}
        onConfirm={handleConfirmCancel}
        title="Cancel Booking"
        description="Are you sure you want to cancel this booking?"
        item={confirmModal.item}
        currency={confirmModal.currency}
        isLoading={confirmModal.isLoading}
      />
      
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, title: '', description: '' })}
        title={successModal.title}
        description={successModal.description}
      />
      
      <LogoutConfirmationModal
        isOpen={logoutModal.isOpen}
        onClose={() => setLogoutModal({ isOpen: false, isLoading: false })}
        onConfirm={handleConfirmLogout}
        isLoading={logoutModal.isLoading}
      />
    </div>
  );
}
